import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useChangeEmail } from '@/hooks/useChangeEmail';

interface ChangeEmailDialogProps {
  advisorId?: string;
  currentEmail: string;
  /** Called with the new email once the change + DB sync succeed. */
  onEmailChanged?: (newEmail: string) => void;
}

function CodeField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <InputOTP maxLength={6} value={value} onChange={onChange} disabled={disabled} autoFocus>
      <InputOTPGroup>
        {Array.from({ length: 6 }).map((_, i) => (
          <InputOTPSlot key={i} index={i} />
        ))}
      </InputOTPGroup>
    </InputOTP>
  );
}

export function ChangeEmailDialog({
  advisorId,
  currentEmail,
  onEmailChanged,
}: ChangeEmailDialogProps) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const flow = useChangeEmail(advisorId, onEmailChanged);

  const close = () => {
    setOpen(false);
    setCode('');
    setEmailInput('');
    flow.reset();
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setOpen(true);
      void flow.begin(); // sends a code to the current (old) email
    } else {
      close();
    }
  };

  // Wrap step submits so a thrown error doesn't reject unhandled — flow.error renders it.
  const submit = (fn: () => Promise<void>, clearCodeOnSuccess: boolean) => async () => {
    try {
      await fn();
      if (clearCodeOnSuccess) setCode('');
    } catch {
      /* surfaced via flow.error */
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => handleOpenChange(true)}
      >
        Change email
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          {flow.step === 'done' ? (
            <>
              <DialogHeader>
                <DialogTitle>Email updated</DialogTitle>
                <DialogDescription>
                  Your account email is now <span className="font-medium">{flow.newEmail}</span>.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={close}>Done</Button>
              </DialogFooter>
            </>
          ) : flow.step === 'newEmail' ? (
            <>
              <DialogHeader>
                <DialogTitle>Enter your new email</DialogTitle>
                <DialogDescription>
                  We&apos;ll send a verification code to confirm you can access it.
                </DialogDescription>
              </DialogHeader>
              <Input
                type="email"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                placeholder="you@example.com"
                autoFocus
              />
              {flow.error && <p className="text-destructive text-sm">{flow.error}</p>}
              <DialogFooter>
                <Button variant="outline" onClick={close} disabled={flow.isSubmitting}>
                  Cancel
                </Button>
                <Button
                  onClick={submit(() => flow.submitNewEmail(emailInput), false)}
                  disabled={flow.isSubmitting || !emailInput.trim()}
                >
                  Send code
                </Button>
              </DialogFooter>
            </>
          ) : flow.step === 'verifyNewEmail' ? (
            <>
              <DialogHeader>
                <DialogTitle>Verify your new email</DialogTitle>
                <DialogDescription>
                  Enter the 6-digit code sent to{' '}
                  <span className="font-medium">{flow.newEmail}</span>.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col items-center gap-3">
                <CodeField value={code} onChange={setCode} disabled={flow.isSubmitting} />
                {flow.error && <p className="text-destructive text-sm">{flow.error}</p>}
                <button
                  type="button"
                  className="text-muted-foreground hover:text-primary text-xs underline"
                  onClick={() => void flow.resend()}
                  disabled={flow.isSubmitting}
                >
                  Resend code
                </button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={close} disabled={flow.isSubmitting}>
                  Cancel
                </Button>
                <Button
                  onClick={submit(() => flow.submitNewEmailCode(code), true)}
                  disabled={flow.isSubmitting || code.length < 6}
                >
                  Confirm change
                </Button>
              </DialogFooter>
            </>
          ) : (
            // 'idle' (starting) and 'reverify'
            <>
              <DialogHeader>
                <DialogTitle>Verify it&apos;s you</DialogTitle>
                <DialogDescription>
                  {flow.step === 'reverify'
                    ? 'For your security, enter the 6-digit code we just sent to your current email address.'
                    : flow.error
                      ? 'We couldn’t start verification.'
                      : `Sending a verification code to ${currentEmail}…`}
                </DialogDescription>
              </DialogHeader>
              {flow.step === 'reverify' ? (
                <div className="flex flex-col items-center gap-3">
                  <CodeField value={code} onChange={setCode} disabled={flow.isSubmitting} />
                  {flow.error && <p className="text-destructive text-sm">{flow.error}</p>}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-primary text-xs underline"
                    onClick={() => void flow.resend()}
                    disabled={flow.isSubmitting}
                  >
                    Resend code
                  </button>
                </div>
              ) : (
                // Still starting, or begin() failed — surface the error so it can't hang silently.
                flow.error && <p className="text-destructive text-sm">{flow.error}</p>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={close} disabled={flow.isSubmitting}>
                  Cancel
                </Button>
                {flow.step !== 'reverify' && flow.error ? (
                  <Button onClick={() => void flow.begin()} disabled={flow.isSubmitting}>
                    Try again
                  </Button>
                ) : (
                  <Button
                    onClick={submit(() => flow.submitReverification(code), true)}
                    disabled={flow.isSubmitting || flow.step !== 'reverify' || code.length < 6}
                  >
                    Verify
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
